"use client";

import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, RefreshCw, ShieldCheck } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import type { CustodyControlPlaneOverview } from "@/lib/work-custody/control-plane";

const VERDICT_LABEL = {
  blocked: "מקור חסר או לא אמין",
  attention: "נדרשת תשומת לב",
  ready: "המקורות מתיישבים",
} as const;
const REFRESH_INTERVAL_MS = 4 * 60_000;
const PROVENANCE_TTL_MS = 10 * 60_000;
const HIDDEN_ERRORS = new Set([
  "custody_auth_required",
  "custody_owner_only",
  "custody_control_plane_activation_required",
]);

export function CustodyControlPlane() {
  const [overview, setOverview] = useState<CustodyControlPlaneOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [expired, setExpired] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        setOverview(null);
        setError("custody_auth_required");
        return;
      }
      const response = await fetch("/api/work-custody/overview", {
        headers: { authorization: `Bearer ${session.access_token}` },
        cache: "no-store",
      });
      const body = await response.json().catch(() => null);
      if (!response.ok) {
        setOverview(null);
        setError(response.status === 401
          ? "custody_auth_required"
          : response.status === 403
            ? "custody_owner_only"
            : body?.error ?? "קריאת משטח המשמורת נכשלה");
        return;
      }
      setOverview(body as CustodyControlPlaneOverview);
      setExpired(false);
    } catch {
      setOverview(null);
      setError("קריאת משטח המשמורת נכשלה");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
    const interval = window.setInterval(() => { void refresh(); }, REFRESH_INTERVAL_MS);
    const onFocus = () => { void refresh(); };
    const onVisibility = () => {
      if (document.visibilityState === "visible") void refresh();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [refresh]);

  useEffect(() => {
    if (!overview) return;
    const expiries = [overview.generatedAt, ...overview.items.map((item) => item.observedAt)]
      .map((value) => Date.parse(value ?? "invalid") + PROVENANCE_TTL_MS)
      .filter(Number.isFinite);
    const expiresAt = expiries.length > 0 ? Math.min(...expiries) : Date.now();
    const timeout = window.setTimeout(() => {
      setExpired(true);
      setOverview(null);
      setError("ראיות המשמורת התיישנו");
      void refresh();
    }, Math.max(0, expiresAt - Date.now()));
    return () => window.clearTimeout(timeout);
  }, [overview, refresh]);

  // Default-off means no custody UI is painted at all. The server route is the
  // authoritative exact-value flag gate, so the private flag is never bundled.
  if ((loading && !overview) || (error && HIDDEN_ERRORS.has(error)) || (expired && overview)) return null;
  if (error || !overview) {
    return (
      <div className="card-flat flex items-center justify-between gap-3 p-4 text-sm text-[var(--role-error)]">
        <span>{error ?? "מקור המשמורת אינו זמין"}</span>
        <button className="btn btn-ghost" type="button" onClick={() => void refresh()}>נסה שוב</button>
      </div>
    );
  }

  const verdictIcon = overview.verdict === "ready"
    ? <CheckCircle2 size={18} />
    : overview.verdict === "blocked" ? <AlertTriangle size={18} /> : <ShieldCheck size={18} />;
  const kpis = [
    ["סביבות עבודה", overview.kpis.worktrees],
    ["מלוכלכות", overview.kpis.dirty],
    ["קומיט מקומי", overview.kpis.committedOnly],
    ["מגובות מרחוק", overview.kpis.remoteBacked],
    ["חוסמים", overview.kpis.blockers],
    ["ראיות סיום", overview.kpis.receipts ?? "לא ידוע"],
    ["Attention כולל", overview.kpis.executionAttentionTotal ?? "לא ידוע"],
  ] as const;

  return (
    <section dir="rtl" className="mb-[14px] space-y-3" aria-label="יסוד משמורת עבודה">
      <div className="card border-s-2 border-s-[var(--app-accent)] p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-sm font-bold text-[var(--app-accent)]">
              {verdictIcon}<span>{VERDICT_LABEL[overview.verdict]}</span>
            </div>
            <div className="mt-2 text-base font-bold text-[var(--fg)]">{overview.nextAction.label}</div>
            <div className="mt-1 text-xs text-[var(--fg-muted)]">
              {overview.nextAction.code} · {overview.nextAction.evidenceRef}
            </div>
          </div>
          <button className="btn btn-ghost" type="button" onClick={() => void refresh()} disabled={loading}>
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />רענן
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 md:grid-cols-3 xl:grid-cols-6">
        {kpis.map(([label, value]) => (
          <div className="card-flat p-3" key={label}>
            <div className="text-xs text-[var(--fg-muted)]">{label}</div>
            <div className="mt-1 text-2xl font-extrabold text-[var(--fg)]">{value}</div>
          </div>
        ))}
      </div>

      <div className="grid gap-3 lg:grid-cols-3">
        <Queue title="תור משמורת" values={[
          ["מלוכלך", overview.queues.custody.dirty], ["קומיט", overview.queues.custody.committed],
          ["מרוחק", overview.queues.custody.remoteBacked], ["ללא שיוך", overview.queues.custody.unmatched],
        ]} />
        <Queue title="תור Git" values={[
          ["חסום", overview.queues.git.blocked], ["מוכן", overview.queues.git.ready],
          ["מגובה", overview.queues.git.backed], ["נכשל", overview.queues.git.failed],
        ]} />
        <Queue title="מצבי יעד" values={[
          ["Courier", overview.targetStates.courierReady],
          ["Courier לא ידוע", overview.targetStates.courierUnknown],
          ["Preview ו־vdev", overview.targetStates.previewReady],
          ["Preview לא ידוע", overview.targetStates.previewUnknown],
          ["Deploy", overview.targetStates.deployReady],
          ["Deploy לא ידוע", overview.targetStates.deployUnknown],
          ["אימות", overview.targetStates.independentlyVerified],
          ["אימות לא ידוע", overview.targetStates.independentlyVerifiedUnknown],
        ]} />
      </div>

      <details className="card-flat p-4">
        <summary className="cursor-pointer text-sm font-bold text-[var(--fg)]">פריטי משמורת וראיות</summary>
        <div className="mt-3 grid gap-3 lg:grid-cols-2">
          <div className="space-y-2">
            {overview.items.length === 0 ? (
              <p className="text-xs text-[var(--role-error)]">אין תצפית סביבות עבודה ולכן אין ירוק</p>
            ) : overview.items.slice(0, 12).map((item) => (
              <div className="rounded-[var(--radius-input)] border border-white/[0.06] p-3" key={item.id}>
                <div className="flex justify-between gap-3 text-xs">
                  <span className="font-semibold text-[var(--fg)]">{item.label}</span>
                  <span className="text-[var(--fg-muted)]">{item.branch ?? "ללא ענף"}</span>
                </div>
                <div className="mt-1 text-[11px] text-[var(--fg-muted)]">
                  dirty {item.dirty ?? "—"} · untracked {item.untracked ?? "—"} · ahead {item.ahead ?? "—"} · behind {item.behind ?? "—"} · system
                </div>
                {item.blockers[0] && <div className="mt-2 text-xs text-[var(--role-warning)]">
                  {item.blockers[0].code} · {item.blockers[0].nextAction}
                </div>}
              </div>
            ))}
          </div>
          <div className="space-y-2">
            {overview.recentReceipts.length === 0 ? (
              <p className="text-xs text-[var(--fg-muted)]">המקור מחובר אך עדיין אין ראיות סיום</p>
            ) : overview.recentReceipts.map((receipt) => (
              <div className="rounded-[var(--radius-input)] border border-white/[0.06] p-3" key={receipt.seq}>
                <div className="flex justify-between gap-3 text-xs">
                  <span className="font-semibold text-[var(--fg)]">{receipt.eventKind}</span>
                  <span className="text-[var(--fg-muted)]">
                    #{receipt.seq} · {receipt.attribution.scope === "tenant" ? `tenant ${receipt.attribution.tenantId}` : "system"}
                  </span>
                </div>
                <div className="mt-1 truncate font-mono text-[11px] text-[var(--fg-muted)]">
                  {receipt.nativeProof ?? receipt.evidenceRef ?? receipt.payloadHash}
                </div>
              </div>
            ))}
          </div>
        </div>
      </details>

      <div className="flex flex-wrap gap-2 text-[11px] text-[var(--fg-muted)]">
        {overview.sources.map((source) => (
          <span className="rounded-full border border-white/[0.06] px-2 py-1" key={source.source}>
            {source.source} · {source.connected
              ? `${source.recordCount} מתוך ${source.totalCount ?? source.recordCount}${source.metricsComplete === false ? " · חלקי" : ""}`
              : source.error ?? "מנותק"} · system
          </span>
        ))}
      </div>
    </section>
  );
}

function Queue({ title, values }: { title: string; values: ReadonlyArray<readonly [string, number | null]> }) {
  return <div className="card-flat p-4"><h3 className="text-sm font-bold text-[var(--fg)]">{title}</h3>
    <div className="mt-3 grid grid-cols-4 gap-2 text-center text-xs">
      {values.map(([label, value]) => <QueueMetric key={label} label={label} value={value} />)}
    </div></div>;
}

function QueueMetric({ label, value }: { label: string; value: number | null }) {
  return <div className="rounded-[var(--radius-input)] border border-white/[0.06] p-2">
    <div className="text-[var(--fg-muted)]">{label}</div>
    <div className="mt-1 text-xl font-extrabold text-[var(--fg)]">{value ?? "לא ידוע"}</div>
  </div>;
}
